import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import toast from 'react-hot-toast';
import { XMarkIcon, BuildingStorefrontIcon } from '@heroicons/react/24/outline';

interface DealerCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  existingDealerInfo?: any; // Dealer info if already associated
  isReadOnly?: boolean; // If true, only show info, no input
}

const DealerCodeModal: React.FC<DealerCodeModalProps> = ({ isOpen, onClose, onSuccess, existingDealerInfo, isReadOnly = false }) => {
  const [dealerCode, setDealerCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [dealerInfo, setDealerInfo] = useState<any>(null);
  
  // Use existing dealer info if provided
  const displayDealerInfo = existingDealerInfo || dealerInfo;

  const validateDealerCodeFn = httpsCallable(functions, 'validateDealerCode');
  const associateDealerFn = httpsCallable(functions, 'associateCustomerWithDealer');

  const handleValidate = async () => {
    if (!dealerCode.trim()) {
      toast.error('Please enter a dealer code');
      return;
    }

    setValidating(true);
    try {
      const result = await validateDealerCodeFn({ dealerCode: dealerCode.trim().toUpperCase() });
      const data = result.data as any;

      if (data.isValid && data.dealer) {
        setDealerInfo(data.dealer);
        toast.success('Dealer code is valid!');
      } else {
        setDealerInfo(null);
        toast.error(data.message || 'Invalid dealer code');
      }
    } catch (error: any) {
      setDealerInfo(null);
      const errorMessage = error.message || 'Failed to validate dealer code';
      toast.error(errorMessage);
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async () => {
    if (!dealerInfo) {
      toast.error('Please validate the dealer code first');
      return;
    }

    setLoading(true);
    try {
      const result = await associateDealerFn({ dealerCode: dealerCode.trim().toUpperCase() });
      const data = result.data as any;

      if (data.success) {
        toast.success('Successfully associated with dealer!');
        onSuccess();
        onClose();
        setDealerCode('');
        setDealerInfo(null);
      } else {
        toast.error(data.message || 'Failed to associate with dealer');
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to associate with dealer';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setDealerCode('');
    setDealerInfo(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '16px'
      }}
      onClick={handleClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '24px',
          maxWidth: '480px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <BuildingStorefrontIcon style={{ width: '24px', height: '24px', color: '#ee4238' }} />
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#1a1a1a' }}>
              {isReadOnly ? 'My Dealer' : 'Add Dealer Code'}
            </h2>
          </div>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <XMarkIcon style={{ width: '24px', height: '24px', color: '#6b7280' }} />
          </button>
        </div>

        {/* Show existing dealer info if read-only */}
        {isReadOnly ? (
          <div
            style={{
              backgroundColor: '#f0fdf4',
              border: '1px solid #86efac',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '20px'
            }}
          >
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600', color: '#166534' }}>
              Your Associated Dealer
            </h3>
            {displayDealerInfo ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <span style={{ fontSize: '14px', color: '#6b7280' }}>Dealer Name: </span>
                  <span style={{ fontSize: '15px', fontWeight: '600', color: '#1a1a1a' }}>
                    {displayDealerInfo.name}
                  </span>
                </div>
                {displayDealerInfo.businessName && (
                  <div>
                    <span style={{ fontSize: '14px', color: '#6b7280' }}>Business Name: </span>
                    <span style={{ fontSize: '15px', fontWeight: '600', color: '#1a1a1a' }}>
                      {displayDealerInfo.businessName}
                    </span>
                  </div>
                )}
                <div>
                  <span style={{ fontSize: '14px', color: '#6b7280' }}>Dealer Code: </span>
                  <span style={{ fontSize: '15px', fontWeight: '700', color: '#10b981', fontFamily: 'monospace' }}>
                    {displayDealerInfo.dealerCode}
                  </span>
                </div>
                {displayDealerInfo.phone && (
                  <div>
                    <span style={{ fontSize: '14px', color: '#6b7280' }}>Contact: </span>
                    <span style={{ fontSize: '15px', fontWeight: '600', color: '#1a1a1a' }}>
                      {displayDealerInfo.phone}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <p style={{ fontSize: '14px', color: '#166534' }}>
                You are linked to a dealer. Dealer details will appear here once available.
              </p>
            )}
          </div>
        ) : (
          <>
            {/* Info Message */}
            <div
              style={{
                backgroundColor: '#fef3c7',
                border: '1px solid #fbbf24',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '20px',
                fontSize: '14px',
                color: '#92400e'
              }}
            >
              <strong>Note:</strong> You can only associate with one dealer. This cannot be changed later.
            </div>

            {/* Input Section */}
            <div style={{ marginBottom: '20px' }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151'
                }}
              >
                Dealer Code
              </label>
              <input
                type="text"
                value={dealerCode}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                  setDealerCode(value);
                  setDealerInfo(null);
                }}
                placeholder="Enter dealer code"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '16px',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  marginBottom: '12px',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#ee4238';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                }}
                disabled={loading || validating}
              />
              <button
                onClick={handleValidate}
                disabled={!dealerCode.trim() || loading || validating}
                style={{
                  width: '100%',
                  padding: '14px',
                  backgroundColor: dealerCode.trim() && !loading && !validating ? '#ee4238' : '#d1d5db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '700',
                  cursor: dealerCode.trim() && !loading && !validating ? 'pointer' : 'not-allowed',
                  transition: 'background-color 0.2s'
                }}
              >
                {validating ? 'Validating...' : 'Validate'}
              </button>
            </div>

            {/* Dealer Info Display (after validation) */}
            {dealerInfo && (
              <div
                style={{
                  backgroundColor: '#f0fdf4',
                  border: '1px solid #86efac',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '20px'
                }}
              >
                <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#166534' }}>
                  Dealer Information
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div>
                    <span style={{ fontSize: '14px', color: '#6b7280' }}>Name: </span>
                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#1a1a1a' }}>
                      {dealerInfo.name}
                    </span>
                  </div>
                  {dealerInfo.businessName && (
                    <div>
                      <span style={{ fontSize: '14px', color: '#6b7280' }}>Business: </span>
                      <span style={{ fontSize: '14px', fontWeight: '600', color: '#1a1a1a' }}>
                        {dealerInfo.businessName}
                      </span>
                    </div>
                  )}
                  <div>
                    <span style={{ fontSize: '14px', color: '#6b7280' }}>Code: </span>
                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#1a1a1a' }}>
                      {dealerInfo.dealerCode}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleClose}
                disabled={loading}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'background-color 0.2s'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!dealerInfo || loading}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: dealerInfo && !loading ? '#ee4238' : '#d1d5db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: dealerInfo && !loading ? 'pointer' : 'not-allowed',
                  transition: 'background-color 0.2s'
                }}
              >
                {loading ? 'Associating...' : 'Confirm & Associate'}
              </button>
            </div>
          </>
        )}

        {/* Close button for read-only mode */}
        {isReadOnly && (
          <button
            onClick={handleClose}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: '#ee4238',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
          >
            Close
          </button>
        )}
      </div>
    </div>
  );
};

export default DealerCodeModal;

